import { 
  collection, 
  doc, 
  setDoc, 
  getDocs, 
  query, 
  where,
  orderBy,
  limit,
  serverTimestamp 
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { WatchlistSnapshot } from '../types';

export class WatchlistSnapshotService {
  private static SNAPSHOTS_COLLECTION = 'watchlistSnapshots';

  static async saveSnapshot(snapshot: Omit<WatchlistSnapshot, 'id'>): Promise<void> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');
    
    const docRef = doc(collection(db, this.SNAPSHOTS_COLLECTION));
    await setDoc(docRef, {
      ...snapshot,
      userId: user.uid,
      timestamp: serverTimestamp()
    });
  }

  static async saveMultipleSnapshots(snapshots: Omit<WatchlistSnapshot, 'id'>[]): Promise<void> {
    const promises = snapshots.map(snapshot => this.saveSnapshot(snapshot));
    await Promise.all(promises);
  }

  static async getLatestSnapshot(symbol: string): Promise<WatchlistSnapshot | null> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');
    
    // Get all snapshots for the symbol and filter client-side
    const q = query(
      collection(db, this.SNAPSHOTS_COLLECTION),
      where('symbol', '==', symbol),
      orderBy('timestamp', 'desc'),
      limit(10)
    );
    
    const querySnapshot = await getDocs(q);
    
    // Find first snapshot that belongs to user or has no userId (legacy)
    for (const doc of querySnapshot.docs) {
      const data = doc.data();
      if (!data.userId || data.userId === user.uid) {
        return {
          ...data,
          id: doc.id,
          userId: data.userId || user.uid,
          timestamp: data.timestamp?.toDate()
        } as WatchlistSnapshot;
      }
    }
    
    return null;
  }

  static async getSnapshotHistory(
    symbol: string, 
    days: number = 30
  ): Promise<WatchlistSnapshot[]> {
    const user = auth.currentUser;
    if (!user) return [];
    
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    
    const q = query(
      collection(db, this.SNAPSHOTS_COLLECTION),
      where('symbol', '==', symbol),
      orderBy('timestamp', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const snapshots: WatchlistSnapshot[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Only include snapshots for current user or without userId (legacy)
      if (!data.userId || data.userId === user.uid) {
        const timestamp = data.timestamp?.toDate();
        if (timestamp && timestamp >= startDate) {
          snapshots.push({
            ...data,
            id: doc.id,
            userId: data.userId || user.uid,
            timestamp
          } as WatchlistSnapshot);
        }
      }
    });
    
    return snapshots;
  }

  static async getSnapshotsBySource(
    source: 'marketsurge' | 'twelvedata' | 'manual' | 'api',
    hours: number = 24
  ): Promise<WatchlistSnapshot[]> {
    const user = auth.currentUser;
    if (!user) return [];
    
    const startDate = new Date();
    startDate.setHours(startDate.getHours() - hours);
    
    // Get all snapshots with the source and filter client-side
    const q = query(
      collection(db, this.SNAPSHOTS_COLLECTION),
      where('source', '==', source),
      orderBy('timestamp', 'desc'),
      limit(100)
    );
    
    const querySnapshot = await getDocs(q);
    const snapshots: WatchlistSnapshot[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Only include snapshots for current user or without userId (legacy)
      if (!data.userId || data.userId === user.uid) {
        const timestamp = data.timestamp?.toDate();
        // Check if within time range
        if (timestamp && timestamp >= startDate) {
          snapshots.push({
            ...data,
            id: doc.id,
            userId: data.userId || user.uid,
            timestamp
          } as WatchlistSnapshot);
        }
      }
    });
    
    return snapshots;
  }

  static async getLatestMarketSurgeImport(): Promise<{ 
    timestamp: Date | null; 
    fileName: string | null;
    symbolCount: number;
  }> {
    const user = auth.currentUser;
    if (!user) return { timestamp: null, fileName: null, symbolCount: 0 };
    
    const q = query(
      collection(db, this.SNAPSHOTS_COLLECTION),
      where('source', '==', 'marketsurge'),
      orderBy('timestamp', 'desc'),
      limit(200) // Get enough to find unique import
    );
    
    const querySnapshot = await getDocs(q);
    
    // Filter for user's snapshots
    const userSnapshots = querySnapshot.docs.filter(doc => {
      const data = doc.data();
      return !data.userId || data.userId === user.uid;
    });
    
    if (userSnapshots.length === 0) {
      return { timestamp: null, fileName: null, symbolCount: 0 };
    }
    
    // Find the latest unique import by fileName or timestamp
    const latestDoc = userSnapshots[0];
    const latestData = latestDoc.data();
    const latestTimestamp = latestData.timestamp?.toDate();
    const fileName = latestData.importFileName || null;
    
    // Count symbols from this import
    let symbolCount = 0;
    userSnapshots.forEach((doc) => {
      const data = doc.data();
      if (data.importFileName === fileName || 
          (data.timestamp?.toDate().getTime() === latestTimestamp?.getTime())) {
        symbolCount++;
      }
    });
    
    return {
      timestamp: latestTimestamp || null,
      fileName,
      symbolCount
    };
  }

  static async getLatestApiUpdate(): Promise<WatchlistSnapshot | null> {
    const user = auth.currentUser;
    if (!user) return null;
    
    const q = query(
      collection(db, this.SNAPSHOTS_COLLECTION),
      where('source', '==', 'api'),
      orderBy('timestamp', 'desc'),
      limit(10)
    );
    
    const querySnapshot = await getDocs(q);
    
    // Find first snapshot that belongs to user
    for (const doc of querySnapshot.docs) {
      const data = doc.data();
      if (!data.userId || data.userId === user.uid) {
        return {
          ...data,
          id: doc.id,
          userId: data.userId || user.uid,
          timestamp: data.timestamp?.toDate()
        } as WatchlistSnapshot;
      }
    }
    
    return null;
  }

  // Get all snapshots for a given import session
  static async getImportSnapshots(
    importFileName: string
  ): Promise<WatchlistSnapshot[]> {
    const user = auth.currentUser;
    if (!user) return [];
    
    const q = query(
      collection(db, this.SNAPSHOTS_COLLECTION),
      where('source', '==', 'marketsurge'),
      where('importFileName', '==', importFileName)
    );
    
    const querySnapshot = await getDocs(q);
    const snapshots: WatchlistSnapshot[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (!data.userId || data.userId === user.uid) {
        snapshots.push({
          ...data,
          id: doc.id,
          userId: data.userId || user.uid,
          timestamp: data.timestamp?.toDate()
        } as WatchlistSnapshot);
      }
    });
    
    return snapshots;
  }
}